import {
  FETCH_NOTIFICATIONS_SUCCESS,
  MARK_NOTIFICATION_READ,
  MARK_ALL_NOTIFICATIONS_READ,
  DELETE_NOTIFICATION,
  DELETE_ALL_NOTIFICATIONS,
  ADD_NOTIFICATION,
  NOTIFICATIONS_REQUEST,
  NOTIFICATIONS_FAILURE,
  SET_NOTIFICATION_COUNTS,
  AUTH_LOGOUT,
} from "../action/types";

const initialState = {
  notifications: [],
  unreadCount: 0,
  unreadCountsByTab: {
    all: 0,
    feedback: 0,
    schedule: 0,
    directive: 0,
  },
  loading: false,
  error: null,
};

const DEFAULT_UNREAD_COUNTS_BY_TAB = {
  all: 0,
  feedback: 0,
  schedule: 0,
  directive: 0,
};

const SCHEDULE_NOTIFICATION_TYPES = new Set([
  "SCHEDULE_URGENT",
  "APPOINTMENT_NEW",
  "APPOINTMENT_CANCELLED",
  "APPOINTMENT_REMINDER",
]);

const BROADCAST_NOTIFICATION_TYPES = new Set(["BROADCAST_NEW"]);

const CATEGORY_TAB_FALLBACK = {
  feedback: "feedback",
  schedule: "schedule",
  event: "schedule",
  system: "directive",
  resident: "directive",
  directive: "directive",
};

const normalizeId = (value) => {
  if (value === undefined || value === null) return "";
  if (typeof value === "object") {
    return String(value.id || value._id || value.toString?.() || "");
  }
  return String(value);
};

const getNotificationId = (notification = {}) =>
  normalizeId(notification.id || notification._id || notification.notificationId);

const getNotificationUserId = (entry = {}) =>
  normalizeId(entry.userId?.id || entry.userId?._id || entry.userId);

const resolveNotificationReadState = (notification = {}, currentUserId) => {
  if (typeof notification.isRead === "boolean") return notification.isRead;

  const userIds = Array.isArray(notification.userIds) ? notification.userIds : [];
  if (userIds.length === 0) return false;

  const normalizedCurrentUserId = normalizeId(currentUserId);
  const currentUserEntry = normalizedCurrentUserId
    ? userIds.find((entry) => getNotificationUserId(entry) === normalizedCurrentUserId)
    : null;

  if (currentUserEntry) return !!currentUserEntry.isRead;

  // API fetches project only the current user's userIds entry. Socket payloads can
  // contain many recipients, so only fall back when the entry is unambiguous.
  if (userIds.length === 1) return !!userIds[0].isRead;

  return false;
};

const normalizeNotification = (notification = {}, currentUserId) => ({
  ...notification,
  id: getNotificationId(notification),
  isRead: resolveNotificationReadState(notification, currentUserId),
});

const getNotificationTabKey = (notification = {}) => {
  if (BROADCAST_NOTIFICATION_TYPES.has(notification.type)) return "directive";
  if (notification.type === "DIRECTIVE_NEW") return "feedback";
  if (SCHEDULE_NOTIFICATION_TYPES.has(notification.type)) return "schedule";
  return CATEGORY_TAB_FALLBACK[notification.category];
};

const toCountNumber = (value, fallback = 0) => {
  const count = Number(value);
  return Number.isFinite(count) ? count : fallback;
};

const normalizeUnreadCountsByTab = (counts, fallback = {}) => {
  const source = counts && typeof counts === "object" ? counts : fallback;
  return Object.keys(DEFAULT_UNREAD_COUNTS_BY_TAB).reduce((result, key) => {
    result[key] = Math.max(
      0,
      toCountNumber(source?.[key], DEFAULT_UNREAD_COUNTS_BY_TAB[key]),
    );
    return result;
  }, {});
};

const changeUnreadCounts = (counts, notification, delta) => {
  if (!notification || notification.isRead) return counts;

  const tabKey = getNotificationTabKey(notification);
  const nextCounts = normalizeUnreadCountsByTab(counts);
  nextCounts.all = Math.max(0, nextCounts.all + delta);

  if (tabKey && nextCounts[tabKey] !== undefined) {
    nextCounts[tabKey] = Math.max(0, nextCounts[tabKey] + delta);
  }

  return nextCounts;
};

const sortNotificationsByCreatedAt = (notifications = []) =>
  [...notifications].sort((a, b) => {
    const aTime = new Date(a.createdAt || 0).getTime();
    const bTime = new Date(b.createdAt || 0).getTime();
    return bTime - aTime;
  });

const notificationsReducer = (state = initialState, action) => {
  const { type, payload } = action;
  const currentUserId = action.meta?.currentUserId;

  switch (type) {
    case NOTIFICATIONS_REQUEST:
      return {
        ...state,
        loading: true,
        error: null,
      };

    case NOTIFICATIONS_FAILURE:
      return {
        ...state,
        loading: false,
        error: payload,
      };

    case AUTH_LOGOUT:
      return initialState;

    case FETCH_NOTIFICATIONS_SUCCESS: {
      const newNotifs = (payload || []).map((notif) =>
        normalizeNotification(notif, currentUserId),
      );
      const nextNotifications = action.meta?.append
        ? [
            ...state.notifications,
            ...newNotifs.filter(
              (newN) => !state.notifications.some((oldN) => oldN.id === newN.id),
            ),
          ]
        : newNotifs;
      const hasUnreadCountsByTab =
        action.meta?.unreadCountsByTab &&
        typeof action.meta.unreadCountsByTab === "object";
      const unreadCountsByTab = hasUnreadCountsByTab
        ? normalizeUnreadCountsByTab(action.meta.unreadCountsByTab)
        : normalizeUnreadCountsByTab({
            ...state.unreadCountsByTab,
            ...(action.meta?.unreadCount !== undefined
              ? { all: action.meta.unreadCount }
              : {}),
          });

      return {
        ...state,
        notifications: sortNotificationsByCreatedAt(nextNotifications),
        unreadCount: hasUnreadCountsByTab
          ? unreadCountsByTab.all
          : toCountNumber(action.meta?.unreadCount, state.unreadCount),
        unreadCountsByTab,
        loading: false,
        error: null,
      };
    }

    case SET_NOTIFICATION_COUNTS: {
      const unreadCountsByTab = normalizeUnreadCountsByTab(
        payload?.unreadCountsByTab || payload,
      );
      const unreadCount = toCountNumber(
        payload?.unreadCount,
        unreadCountsByTab.all,
      );

      return {
        ...state,
        unreadCount,
        unreadCountsByTab: {
          ...unreadCountsByTab,
          all: unreadCount,
        },
      };
    }

    case ADD_NOTIFICATION:
      // Prevent duplicates
      if (
        state.notifications.some(
          (n) => getNotificationId(n) === getNotificationId(payload),
        )
      ) {
        return state;
      }
      const notification = normalizeNotification(payload, currentUserId);
      const unreadCountsByTab = changeUnreadCounts(
        state.unreadCountsByTab,
        notification,
        1,
      );

      return {
        ...state,
        notifications: sortNotificationsByCreatedAt([
          notification,
          ...state.notifications,
        ]),
        unreadCount: unreadCountsByTab.all,
        unreadCountsByTab,
      };

    case MARK_NOTIFICATION_READ: {
      const notification = state.notifications.find(
        (notif) => notif.id === payload,
      );
      const unreadCountsByTab = changeUnreadCounts(
        state.unreadCountsByTab,
        notification,
        -1,
      );
      return {
        ...state,
        notifications: state.notifications.map((notif) =>
          notif.id === payload ? { ...notif, isRead: true } : notif,
        ),
        unreadCount: unreadCountsByTab.all,
        unreadCountsByTab,
      };
    }

    case MARK_ALL_NOTIFICATIONS_READ:
      return {
        ...state,
        notifications: state.notifications.map((notif) => ({
          ...notif,
          isRead: true,
        })),
        unreadCount: 0,
        unreadCountsByTab: { ...DEFAULT_UNREAD_COUNTS_BY_TAB },
      };

    case DELETE_NOTIFICATION: {
      const notification = state.notifications.find(
        (notif) => notif.id === payload,
      );
      const unreadCountsByTab = changeUnreadCounts(
        state.unreadCountsByTab,
        notification,
        -1,
      );
      return {
        ...state,
        notifications: state.notifications.filter(
          (notif) => notif.id !== payload,
        ),
        unreadCount: unreadCountsByTab.all,
        unreadCountsByTab,
      };
    }

    case DELETE_ALL_NOTIFICATIONS:
      return {
        ...state,
        notifications: [],
        unreadCount: 0,
        unreadCountsByTab: { ...DEFAULT_UNREAD_COUNTS_BY_TAB },
      };

    default:
      return state;
  }
};

export default notificationsReducer;
